import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import {
  ActionList, Details, Popover, useDetails
} from '@primer/react';

import { DROPDOWN_DIRECTIONS } from '../../../constants/primer.constants';

import Button from '../Button/Button';
import Label from '../Labels/Label';

import CaretDownIcon from '../icons/CaretDownIcon';
import CaretUpIcon from '../icons/CaretUpIcon';
import Icon from '../icons/Icon';
import { getThemeColor } from '../../../utils/theme-utils';
import SearchInput from '../TextInputs/SearchInput/SearchInput';

const BasicDropdownItem = ({
  id, label, render, isSelected, onValueSelected
}) => {
  if (isFunction(render)) {
    return (
      <ActionList.Item
        className={isSelected ? 'bg-border-dark' : 'bg-white hover:bg-border-light'}
        value={id}
      >
        {
          render({
            id, label, isSelected, onValueSelected
          })
        }
      </ActionList.Item>
    )
  }
  return (
    <ActionList.Item
      className={isSelected ? 'bg-border-dark' : 'bg-white hover:bg-border-light'}
      value={id}
      onClick={() => onValueSelected(id)}
    >
      {label}
    </ActionList.Item>
  )
}

// TODO - Consider replacing with Primer Select (Check with Design)
export const BasicDropdown = ({
  values,
  selectedValue,
  onValueSelected,
  search = false,
  className = '',
  icon = undefined,
  isDisabled = false,
  hideCaret = false,
  hideBorder = false,
  dropdownLabel = 'Select',
  bgColorOpen = 'border-dark',
  bgColorClosed = 'white',
  textColor = 'brand',
  iconFill = 'white',
  iconClass = '',
  iconSize = 32,
  buttonClass = '',
  popoverClass = '',
  direction = DROPDOWN_DIRECTIONS[0],
  useTailwindIconSizing = false,
  hideLabelInSmallViewports = true, // Added to preserve legacy logic
  buttonLabelSize = 'lg',
}) => {
  const { getDetailsProps, open, setOpen } = useDetails({ closeOnOutsideClick: true });

  const [listItems, setListItems] = useState([])

  useEffect(() => {
    setListItems(values)
  }, [values])

  const selectedLabel = useMemo(() => {
    if (!values || !values.length) {
      return null;
    }
    return values.find(({ id }) => id === selectedValue)?.label;
  }, [selectedValue, values]);

  const displayLabel = useMemo(() => `${dropdownLabel ? `${dropdownLabel} ` : ''}${selectedLabel || ''}`, [dropdownLabel, selectedLabel])
  const buttonProps = hideBorder ? {
    border: 0,
    padding: 3,
    bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    ':hover:not([disabled])': {
      bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    },
    ':active:not([disabled])': {
      bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    },
    boxShadow: 0,
  } : {};
  const iconFillColor = getThemeColor(iconFill) || iconFill;

  if (isDisabled) {
    return <Button variant={hideBorder ? 'invisible' : 'default'} disabled><span className="text-lg">{displayLabel}</span></Button>
  }

  const handleSearchInput = (e) => {
    setListItems(values.filter(item => item.label.includes(e)))
  }

  return (
    <Details
      className={
        classNames(
          'brand-select w-min appearance-none cursor-pointer',
          { [`bg-${bgColorOpen}`]: open },
          { [`bg-${bgColorClosed}`]: !open },
          className
        )
      }
      {...getDetailsProps()}
    >
      <Button
        variant={hideBorder ? 'invisible' : 'default'}
        as="summary"
        sx={buttonProps}
      >
        <div className={classNames('flex justify-between items-center', buttonClass)}>
          {
            icon ? (
              <Icon className={iconClass} fill={iconFillColor} icon={icon} size={iconSize} useTailwindSizing={useTailwindIconSizing} />
            ) : null
          }
          {
            displayLabel?.length ? (
              <Label 
                className={classNames(
                  { 'hidden lg:inline-block': hideLabelInSmallViewports },
                  { 'inline-block': !hideLabelInSmallViewports },
                )}
                size={buttonLabelSize} 
                color={textColor}
              >
                {displayLabel}
              </Label>
            ) : null
          }
          {
            hideCaret ? null : (
              <div className="ml-2">
                {open ? <CaretUpIcon fill={textColor} /> : <CaretDownIcon fill={textColor} />}
              </div>
            )
          }
        </div>
      </Button>
      {open && <Popover
        style={{ zIndex: 50 }}
        className={classNames('text-brand', popoverClass)}
        sx={{ mt: 1 }}
        caret={direction}
        open={open}
      >
        {
          search && listItems.length > 7 && <SearchInput
            className="w-full"
            onChange={e => handleSearchInput(e.target.value)}
          />
        }
        <Popover.Content className={(listItems.length > 7) ? 'bg-white h-60' : 'bg-white'} style={{ left: '0px', overflow: 'scroll' }} sx={{ p: 1 }}>
          {
            (listItems?.length) ? listItems.map(({ id, label, render }) => (
              label !== ''
                ? <BasicDropdownItem
                  key={id}
                  id={id}
                  label={label}
                  render={render}
                  isSelected={id === selectedValue}
                  onValueSelected={(selectedId) => {
                    if (isFunction(onValueSelected)) {
                      onValueSelected(selectedId);
                    }
                    setOpen(false);
                  }}
                />
                : <ActionList.Divider key={id} />
            )) : null
          }
        </Popover.Content>
      </Popover>}
    </Details>
  )
}

export default BasicDropdown;
